import PDFBuilder, { fmtDate, fmtDateFromISO, formatCI } from "../../utils/pdfBuilder.js";

const CONDITION_LABELS = {
  buna: "Buna",
  satisfacatoare: "Satisfacatoare",
  uzura_normala: "Uzura normala",
};

const STARE_LABELS = {
  buna: "Buna",
  satisfacatoare: "Satisfacatoare",
  uzura_normala: "Uzura normala",
};

const UTILITY_DEFS = [
  { label: "Energie electrica", key: "energieElectrica", unit: "kWh" },
  { label: "Gaz natural", key: "gaz", unit: "mc" },
  { label: "Apa calda", key: "apaCalda", unit: "mc" },
  { label: "Apa rece", key: "apaRece", unit: "mc" },
  { label: "Incalzire", key: "incalzire", unit: "Gcal" },
];

export default function buildComodatPVPDF(data, filename) {
  const pdf = new PDFBuilder();
  const today = fmtDate(new Date());
  const pvDate = data.pvDate ? fmtDateFromISO(data.pvDate) : today;

  const rooms = data.pvRooms || [];
  const utilities = data.pvUtilities || {};
  const hasUtilities = Object.values(utilities).some((v) => v && String(v).trim());
  const hasItems = rooms.some((r) => (r.items || []).length > 0);

  let sectionNr = 3;

  // Header
  pdf.title(
    "Proces Verbal de Predare-Primire",
    `Anexa la Contractul de Comodat din data de ${today}`
  );
  pdf.p(`Incheiat astazi, ${pvDate}`, { indent: 0 });
  pdf.space(2);

  // 1. PARTILE
  pdf.section("1. PARTILE");

  if (data.comodantPJ) {
    pdf.pMixed([
      { text: data.comodantFirma || "________________________", bold: true },
      { text: `, CUI ${data.comodantCUI || "____________"}, cu sediul in ${data.comodantSediu || "________________________"}, reprezentata prin ` },
      { text: data.comodantReprezentant || "________________________", bold: true },
      { text: ", in calitate de " },
      { text: "Comodant", bold: true },
      { text: "," },
    ]);
  } else {
    const ciComodantFmt = data.comodantCI ? formatCI(data.comodantCI) : "___ ___";
    pdf.pMixed([
      { text: data.comodantNume || "________________________", bold: true },
      { text: `, CNP ${data.comodantCNP || "____________"}, CI seria ${ciComodantFmt}, domiciliat in ${data.comodantAdresa || "________________________"}, in calitate de ` },
      { text: "Comodant", bold: true },
      { text: "," },
    ]);
  }

  pdf.italic("si");

  if (data.comodatarPJ) {
    pdf.pMixed([
      { text: data.comodatarFirma || "________________________", bold: true },
      { text: `, CUI ${data.comodatarCUI || "____________"}, cu sediul in ${data.comodatarSediu || "________________________"}, reprezentata prin ` },
      { text: data.comodatarReprezentant || "________________________", bold: true },
      { text: ", in calitate de " },
      { text: "Comodatar", bold: true },
      { text: "," },
    ]);
  } else {
    const ciComodatarFmt = data.comodatarCI ? formatCI(data.comodatarCI) : "___ ___";
    pdf.pMixed([
      { text: data.comodatarNume || "________________________", bold: true },
      { text: `, CNP ${data.comodatarCNP || "____________"}, CI seria ${ciComodatarFmt}, domiciliat in ${data.comodatarAdresa || "________________________"}, in calitate de ` },
      { text: "Comodatar", bold: true },
      { text: "," },
    ]);
  }

  pdf.intro("s-a procedat la predarea-primirea imobilului in urmatoarele conditii:");

  // 2. IMOBILUL
  pdf.section("2. IMOBILUL");

  const etajText = data.imobilEtaj ? `, etaj ${data.imobilEtaj}` : "";

  pdf.pMixed([
    { text: "Imobilul situat in " },
    { text: data.imobilAdresa || "________________________", bold: true },
    { text: `, compus din ${data.imobilCamere || "___"} camere, suprafata utila ${data.imobilSuprafata || "___"} mp${etajText}, predat ${data.mobilat ? "mobilat si utilat" : "nemobilat"}.` },
  ]);

  const stareLabel = STARE_LABELS[data.pvStareGenerala] || data.pvStareGenerala || "Buna";
  pdf.pMixed([
    { text: "Starea generala a imobilului la momentul predarii: " },
    { text: stareLabel, bold: true },
    { text: "." },
  ]);

  // 3. INVENTAR PE CAMERE
  if (rooms.length > 0) {
    pdf.section(`${sectionNr++}. INVENTAR PE CAMERE`);

    rooms.forEach((room) => {
      // Room heading
      pdf.pMixed([{ text: room.label, bold: true }]);

      const items = room.items || [];
      if (items.length > 0) {
        // Table header row
        pdf.pMixed([
          { text: "Nr.", bold: true },
          { text: "    " },
          { text: "Obiect", bold: true },
          { text: "                              " },
          { text: "Stare", bold: true },
        ], { indent: 5 });

        items.forEach((item, idx) => {
          const condLabel = CONDITION_LABELS[item.condition] || item.condition || "-";
          pdf.pMixed([
            { text: `${idx + 1}.` },
            { text: `  ${item.name || "-"}` },
            { text: ` — ${condLabel}` },
          ], { indent: 5 });
        });
      } else {
        pdf.p("Fara obiecte inventariate.", { indent: 5 });
      }

      if (room.observations) {
        pdf.p(`Observatii: ${room.observations}`, { indent: 5 });
      }

      pdf.space(2);
    });

    if (data.mobilat && !hasItems) {
      pdf.p("Mobilierul si dotarile existente nu sunt inventariate individual.");
    }
  }

  // CITIRI CONTOARE
  if (hasUtilities) {
    pdf.section(`${sectionNr++}. CITIRI CONTOARE`);

    // Table header
    pdf.pMixed([
      { text: "Utilitate", bold: true },
      { text: "                    " },
      { text: "Index", bold: true },
      { text: "          " },
      { text: "U.M.", bold: true },
    ], { indent: 5 });

    UTILITY_DEFS.filter((u) => utilities[u.key]).forEach((u) => {
      pdf.pMixed([
        { text: u.label },
        { text: ": " },
        { text: String(utilities[u.key]), bold: true },
        { text: ` ${u.unit}` },
      ], { indent: 5 });
    });

    pdf.space(2);
  }

  // OBSERVATII GENERALE
  if (data.pvObservatiiGenerale) {
    pdf.section(`${sectionNr++}. OBSERVATII GENERALE`);
    pdf.p(data.pvObservatiiGenerale);
  }

  // DECLARATIE
  pdf.section(`${sectionNr++}. DECLARATIE`);
  pdf.p("Subsemnatii declara ca imobilul a fost predat/primit in starea descrisa mai sus, iar inventarul corespunde situatiei reale de la data predarii. Orice diferente constatate ulterior nu pot fi imputate partii care preda imobilul, daca nu sunt consemnate in prezentul proces-verbal.");

  // Signatures
  const comodantName = pdf.getPartyNamePFPJ(data, "comodant", data.comodantPJ);
  const comodatarName = pdf.getPartyNamePFPJ(data, "comodatar", data.comodatarPJ);

  pdf.signatures(
    { role: "AM PREDAT — COMODANT", name: comodantName, date: pvDate },
    { role: "AM PRIMIT — COMODATAR", name: comodatarName, date: pvDate },
  );

  pdf.save(filename);
}
